import React from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { StepComponentProps } from "./types";

export const ReferralStep: React.FC<StepComponentProps> = ({
  formData,
  control,
  uplineName = "",
  validatingReferral = false,
  referralError = "",
  isReferralFromUrl = false,
}) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      <div>
        <label
          className="block font-semibold mb-2 text-primary dark:text-primary text-base"
          htmlFor="referralCode"
        >
          {t("auth.registration.referralCode")} *
        </label>
        <Controller
          name="referralCode"
          control={control}
          rules={{
            required:
              t("auth.registration.referralCodeRequired") ||
              "Referral code is required",
            pattern: {
              value: /^NS[A-Z0-9]{8}$/,
              message:
                t("auth.registration.invalidReferralCode") ||
                "Referral code must start with NS followed by 8 alphanumeric characters (e.g., NSBXZ4XUZQ)",
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <>
              <input
                {...field}
                id="referralCode"
                type="text"
                placeholder="NSBXZ4XUZQ"
                maxLength={10}
                disabled={isReferralFromUrl}
                className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/60 text-base font-medium text-[var(--text-primary)] ${
                  error || referralError
                    ? "border-red-400"
                    : uplineName
                    ? "border-green-500 bg-green-50 dark:bg-green-900/20"
                    : "border-[var(--border-light)]"
                } ${
                  isReferralFromUrl
                    ? "bg-[var(--bg-card)] cursor-not-allowed"
                    : "bg-[var(--bg-surface)]"
                }`}
                style={{ textTransform: "uppercase" }}
              />
              {validatingReferral && (
                <div className="text-blue-600 dark:text-blue-400 text-sm mt-2 flex items-center gap-2 font-medium">
                  <svg
                    className="animate-spin h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  {t("auth.registration.validatingReferral") || "Validating..."}
                </div>
              )}
              {error && (
                <div className="text-red-500 text-sm mt-2 flex items-center gap-1">
                  <svg
                    className="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {error.message}
                </div>
              )}
              {referralError && !error && (
                <div className="text-red-500 text-sm mt-2 flex items-center gap-1 font-medium">
                  <svg
                    className="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {referralError}
                </div>
              )}
            </>
          )}
        />
      </div>

      {/* UPLINE DETAILS CARD - Highly Visible */}
      {uplineName && !validatingReferral && (
        <div className="mt-4 p-5 bg-gradient-to-br from-green-100 via-emerald-50 to-green-100 dark:from-green-900/30 dark:via-emerald-900/20 dark:to-green-900/30 border-3 border-green-400 dark:border-green-600 rounded-2xl shadow-lg animate-fadeIn">
          {/* Header with Avatar */}
          <div className="flex items-center gap-3 mb-4 pb-4 border-b-2 border-green-300 dark:border-green-700">
            <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center shadow-lg ring-4 ring-green-200 dark:ring-green-800">
              <svg
                className="w-8 h-8 text-white"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="flex-1">
              <div className="text-green-700 dark:text-green-400 text-xs font-bold uppercase tracking-wider mb-1">
                {t("auth.registration.yourUpline") || "YOUR UPLINE / REFERRER"}
              </div>
              <div className="text-green-900 dark:text-green-100 text-2xl font-bold">
                {uplineName}
              </div>
            </div>
            <div className="flex-shrink-0">
              <svg
                className="w-10 h-10 text-green-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          </div>

          {/* Referral Code Display */}
          <div className="bg-[var(--bg-card)] dark:bg-[var(--bg-card)] rounded-lg p-4 mb-3 shadow-sm">
            <div className="text-green-700 dark:text-green-400 text-sm font-semibold mb-1.5">
              {t("auth.registration.referralCodeVerified") ||
                "Referral Code (Verified)"}
            </div>
            <div className="text-green-900 dark:text-green-100 font-mono font-bold text-xl tracking-wider">
              {formData.referralCode}
            </div>
          </div>

          {/* Info Message */}
          <div className="flex items-start gap-3 bg-[var(--bg-card)] dark:bg-[var(--bg-card)] rounded-lg p-4 shadow-sm">
            <svg
              className="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            <div className="text-green-800 dark:text-green-200 text-sm leading-relaxed">
              {isReferralFromUrl ? (
                <span className="font-semibold">
                  {t("auth.registration.referralPreFilled") ||
                    "Referral code pre-filled from link. This person will be your upline and guide you."}
                </span>
              ) : (
                <span className="font-medium">
                  {t("auth.registration.uplineWillGuide") ||
                    "This person referred you and will be your upline. They will guide and support you in your journey."}
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
